import type { TopoNode } from "./topologyTypes";

// Isometric projection for the architecture canvas.
//
// Standard 2:1 "video-game" isometric: one step along the grid's column axis
// moves right-and-down, one step along the row axis moves left-and-down. Tiers
// are rows, so the diagram reads top-left (Internet) to bottom-right
// (endpoints) the way a rack elevation reads top to bottom.
//
// SELF-CONTAINED verbatim port from the example app's modules/topology/iso.ts.

/** Half-width of one grid step, in user units. */
export const CELL_X = 92;
/** Half-height of one grid step. CELL_X / CELL_Y ≈ 2 gives the 2:1 look. */
export const CELL_Y = 48;

/** Tile (the diamond a node stands on). */
export const TILE_HW = 42;
export const TILE_HH = 22;
/** Extrusion depth of the tile — what makes it a solid, not an outline. */
export const TILE_DEPTH = 14;

/** Where the glyph floats relative to the tile's centre. */
export const GLYPH_LIFT = 34;
export const GLYPH_SIZE = 30;

export type Point = { x: number; y: number };

export function project(col: number, row: number): Point {
  return { x: (col - row) * CELL_X, y: (col + row) * CELL_Y };
}

export function projectNode(node: Pick<TopoNode, "col" | "row">): Point {
  return project(node.col, node.row);
}

/** Screen → grid. Returns fractional coordinates; round to snap. */
export function unproject(x: number, y: number): { col: number; row: number } {
  return {
    col: (x / CELL_X + y / CELL_Y) / 2,
    row: (y / CELL_Y - x / CELL_X) / 2,
  };
}

/** The four corners of a node's tile top face, as an SVG points string. */
export function tilePoints(p: Point): string {
  return [
    `${p.x},${p.y - TILE_HH}`,
    `${p.x + TILE_HW},${p.y}`,
    `${p.x},${p.y + TILE_HH}`,
    `${p.x - TILE_HW},${p.y}`,
  ].join(" ");
}

export function tileLeftFace(p: Point): string {
  return [
    `${p.x - TILE_HW},${p.y}`,
    `${p.x},${p.y + TILE_HH}`,
    `${p.x},${p.y + TILE_HH + TILE_DEPTH}`,
    `${p.x - TILE_HW},${p.y + TILE_DEPTH}`,
  ].join(" ");
}

export function tileRightFace(p: Point): string {
  return [
    `${p.x + TILE_HW},${p.y}`,
    `${p.x},${p.y + TILE_HH}`,
    `${p.x},${p.y + TILE_HH + TILE_DEPTH}`,
    `${p.x + TILE_HW},${p.y + TILE_DEPTH}`,
  ].join(" ");
}

/** Draw order: things further "south" on the isometric plane must paint last
 *  so they occlude what's behind them. */
export function depthSort<T extends Pick<TopoNode, "col" | "row">>(nodes: T[]): T[] {
  return [...nodes].sort((a, b) => a.col + a.row - (b.col + b.row) || a.col - b.col);
}

export type Viewport = { minX: number; minY: number; width: number; height: number };

const PAD_TOP = GLYPH_LIFT + GLYPH_SIZE + 34;
const PAD_BOTTOM = TILE_HH + TILE_DEPTH + 46;
const PAD_RIGHT = TILE_HW + 78;
/** Wider than the right: the tier captions hang off the left of each band. */
const PAD_LEFT = TILE_HW + 180;

export function boundsOf(nodes: Pick<TopoNode, "col" | "row">[]): Viewport {
  if (nodes.length === 0) return { minX: -400, minY: -200, width: 800, height: 400 };
  const pts = nodes.map((n) => project(n.col, n.row));
  const minX = Math.min(...pts.map((p) => p.x)) - PAD_LEFT;
  const maxX = Math.max(...pts.map((p) => p.x)) + PAD_RIGHT;
  const minY = Math.min(...pts.map((p) => p.y)) - PAD_TOP;
  const maxY = Math.max(...pts.map((p) => p.y)) + PAD_BOTTOM;
  return { minX, minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
}

/** Isometric-aware link routing: one leg along each grid axis so connectors
 *  sit on the same 30° lines as the tiles instead of cutting across them. */
export function routeLink(from: Pick<TopoNode, "col" | "row">, to: Pick<TopoNode, "col" | "row">): string {
  const a = project(from.col, from.row);
  const b = project(to.col, to.row);
  const bend = project(to.col, from.row);
  const straight = from.col === to.col || from.row === to.row;
  return straight
    ? `M ${a.x} ${a.y} L ${b.x} ${b.y}`
    : `M ${a.x} ${a.y} L ${bend.x} ${bend.y} L ${b.x} ${b.y}`;
}

/** Finds the first unoccupied cell on a given row, scanning right. */
export function freeCell(nodes: Pick<TopoNode, "col" | "row">[], row: number): { col: number; row: number } {
  const taken = new Set(nodes.filter((n) => n.row === row).map((n) => n.col));
  let col = 0;
  while (taken.has(col)) col++;
  return { col, row };
}
